import { LEASE_OPTIONS } from "modules/LeadsDashboard/hooks";
import { QUESTION_STATUSES_DEFAULT_STATUSES, setLeadsSnoozedFilters, useLeadsSnoozedFilters } from "modules/LeadsDashboard/Leads/SnoozedLeads/hooks";
import { LEADS_SNOOZED_STORAGE_KEYS } from "modules/LeadsDashboard/storageKeys";
import { setPreference } from "utils/common";

import type { OnChangeFn, PaginationState } from "@tanstack/react-table";
import type { DropdownItemProps } from "components/Dropdown";
import type { SNOOZE_STATUSES, SNOOZE_TYPES } from "models";
import type { PAGE_SIZE } from "utils/common";

type UseSnoozedLeadsTableFilterHandlersProps = {
  snoozeStatusOptions: DropdownItemProps<string | SNOOZE_STATUSES>[];
  leaseCompanyOptions: DropdownItemProps<string>[];
  snoozeTypeOptions: DropdownItemProps<string | SNOOZE_TYPES>[];
};

export const useSnoozedLeadsTableFilterHandlers = ({ snoozeTypeOptions, leaseCompanyOptions, snoozeStatusOptions }: UseSnoozedLeadsTableFilterHandlersProps) => {
  const snoozedFilters = useLeadsSnoozedFilters();
  const { page: currentPage, page_size: pageSize, statuses, snooze_type_id, questions } = snoozedFilters;

  const onPageChange = (data: { activePage: number }) => setLeadsSnoozedFilters({ ...snoozedFilters, page: data.activePage });
  const handlePaginationSelect: OnChangeFn<PaginationState> = updater => {
    const newState = typeof updater === "function" ? updater({ pageIndex: currentPage - 1, pageSize }) : updater;
    onPageChange({ activePage: newState.pageIndex + 1 });
  };

  const handlePageSizeChange = (value: PAGE_SIZE) => {
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.PageSizePreference, value);
    setLeadsSnoozedFilters({ ...snoozedFilters, page_size: value, page: 1 });
  };

  const handleQuestionResultStatusSelect = (questionStatuses: string[]) => {
    const isCurrentlyAll = !statuses || statuses.length === QUESTION_STATUSES_DEFAULT_STATUSES.length;
    const selectedStatuses = questionStatuses.map(Number).filter(Boolean);

    const isSelectingAll = questionStatuses.includes("all") && !isCurrentlyAll;
    const isNoneOrEverySelected = !selectedStatuses.length || selectedStatuses.length === QUESTION_STATUSES_DEFAULT_STATUSES.length;

    const newStatuses = isSelectingAll || isNoneOrEverySelected ? null : selectedStatuses;
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.QuestionStatusPreference, newStatuses);
    setLeadsSnoozedFilters({ ...snoozedFilters, statuses: newStatuses, page: 1 });
  };

  const handleOnLeaseSelect = (leaseCompany: string[]) => {
    const leaseCompanyOption = leaseCompanyOptions.find(option => option.value === leaseCompany[0]) || null;

    let is_lease_company: boolean | null = null;
    if (leaseCompanyOption && leaseCompanyOption.value !== LEASE_OPTIONS.All) is_lease_company = leaseCompanyOption.value === LEASE_OPTIONS.Lease;

    setPreference(LEADS_SNOOZED_STORAGE_KEYS.RegVinPreference, is_lease_company);
    setLeadsSnoozedFilters({ ...snoozedFilters, is_lease_company, page: 1 });
  };

  const handleOnTaskSelect = (task: string[]) => {
    const isResetNotSelected = task.includes("all") && snooze_type_id !== null;
    const tasks = isResetNotSelected ? null : snoozeTypeOptions.find(option => option.value === Number(task[0]));
    const taskOption = tasks ? (tasks.value as SNOOZE_TYPES) : null;

    setPreference(LEADS_SNOOZED_STORAGE_KEYS.TaskPreference, taskOption);
    setLeadsSnoozedFilters({ ...snoozedFilters, snooze_type_id: taskOption, page: 1 });
  };

  const handleOnSnoozeStatusSelect = (snoozeStatus: string[]) => {
    const isResetNotSelected = snoozeStatus.includes("all") && statuses !== null;
    const newStatuses = isResetNotSelected ? null : snoozeStatusOptions.find(option => option.value === Number(snoozeStatus[0]));
    const snoozeStatusOption = newStatuses ? (newStatuses.value as SNOOZE_STATUSES) : null;

    setPreference(LEADS_SNOOZED_STORAGE_KEYS.SnoozeStatusPreference, snoozeStatusOption);
    setLeadsSnoozedFilters({ ...snoozedFilters, snooze_status_id: snoozeStatusOption, page: 1 });
  };

  const handleOnCustomerOkUserSelect = (customerOkUser: string[]) => {
    const isResetSelected = customerOkUser.includes("all") && statuses !== null;

    setPreference(LEADS_SNOOZED_STORAGE_KEYS.CustomerOkUserPreference, !isResetSelected);
    setLeadsSnoozedFilters({ ...snoozedFilters, last_customer_ok_is_me: !isResetSelected, page: 1 });
  };

  const handleOnQuestionSelect = (value: string[]) => {
    const isResetNotSelected = value.includes("all") && questions !== null;
    const newQuestions = isResetNotSelected ? null : value.filter(el => el !== "all").map(el => Number(el.split("-")[1]));

    setPreference(LEADS_SNOOZED_STORAGE_KEYS.InterventionQuestionResultPreference, newQuestions);
    setLeadsSnoozedFilters({ ...snoozedFilters, questions: newQuestions, page: 1 });
  };

  const handleAppointmentDateRangeChange = ([start, end]: [Date | null, Date | null]) => {
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.AppointmentDatePreference, { start, end });
    setLeadsSnoozedFilters({ ...snoozedFilters, appointment_date_from: start, appointment_date_to: end, page: 1 });
  };

  const handleSnoozedDateRangeChange = ([start, end]: [Date | null, Date | null]) => {
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.SnoozeDatePreference, { start, end });
    setLeadsSnoozedFilters({ ...snoozedFilters, snooze_date_from: start, snooze_date_to: end, page: 1 });
  };

  const handleUpdatedOnDateRangeChange = ([start, end]: [Date | null, Date | null]) => {
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.UpdatedOnDatePreference, { start, end });
    setLeadsSnoozedFilters({ ...snoozedFilters, updated_on_from: start, updated_on_to: end, page: 1 });
  };

  return {
    handleOnTaskSelect,
    handleOnLeaseSelect,
    handlePageSizeChange,
    handlePaginationSelect,
    handleOnQuestionSelect,
    handleOnSnoozeStatusSelect,
    handleSnoozedDateRangeChange,
    handleOnCustomerOkUserSelect,
    handleAppointmentDateRangeChange,
    handleQuestionResultStatusSelect,
    handleUpdatedOnDateRangeChange
  };
};
